<template>
    <div class="pdf-viewer">
        <mc-loading
            :loading="loading"
            message="PDF加载中..."
        >
            <div
                v-if="error"
                class="pdf-viewer__error"
            >
                <p>{{ error }}</p>
                <el-button
                    size="small"
                    type="primary"
                    @click="loadPdf"
                >
                    重新加载
                </el-button>
            </div>
            <div
                v-else
                ref="pdfContainer"
                class="pdf-viewer__container"
            >
                <canvas
                    v-for="page in totalPages"
                    :key="page"
                    :ref="`canvas-${page}`"
                    class="pdf-viewer__page"
                ></canvas>
            </div>
            <div class="pdf-viewer__download">
                <img
                    src="https://p0.meituan.net/mallimages/ba6aeaa157e942754234b4a531a85fec9638.png"
                    class="pdf-viewer__download-icon"
                />
                <span class="pdf-viewer__download-text">{{ fileName || '函件.pdf' }}</span>
                <mc-link
                    :href="url"
                >
                    查看源文件
                </mc-link>
            </div>
        </mc-loading>
    </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import { divide, multiply } from '@lsfe/tools';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default {
    name: 'PdfViewer',
    props: {
        fileName: {
            type: String,
            default: '',
        },
        url: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loading: false,
            error: '',
            totalPages: 0,
            pdfDoc: null,
        };
    },
    watch: {
        url: {
            handler(val) {
                if (val) {
                    this.loadPdf();
                }
            },
            immediate: true,
        },
    },
    beforeDestroy() {
        if (this.pdfDoc) {
            this.pdfDoc.destroy();
            this.pdfDoc = null;
        }
    },
    methods: {
        async loadPdf() {
            if (!this.url) {
                return;
            }

            this.loading = true;
            this.error = '';
            this.totalPages = 0;

            try {
                const pdf = await pdfjsLib.getDocument(this.url).promise;
                this.pdfDoc = pdf;
                this.totalPages = pdf.numPages;

                await this.$nextTick();
                for (let pageNum = 1; pageNum <= this.totalPages; pageNum += 1) {
                    await this.renderPage(pageNum);
                }
            } catch (err) {
                this.error = `PDF加载失败: ${err.message || '未知错误'}`;
            } finally {
                this.loading = false;
            }
        },

        async renderPage(pageNum) {
            try {
                const page = await this.pdfDoc.getPage(pageNum);
                const containerWidth = this.$refs.pdfContainer?.clientWidth || window.innerWidth;
                const baseWidth = page.getViewport({ scale: 1 }).width;
                const fitScale = divide(containerWidth, baseWidth);
                const viewport = page.getViewport({
                    scale: Math.min(2, fitScale),
                });

                const canvasRef = this.$refs[`canvas-${pageNum}`];
                const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
                if (!canvas) {
                    return;
                }

                const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
                const width = Math.floor(viewport.width);
                const height = Math.floor(viewport.height);

                canvas.width = multiply(width, pixelRatio);
                canvas.height = multiply(height, pixelRatio);
                canvas.style.width = `${width}px`;
                canvas.style.height = '';

                await page.render({
                    canvasContext: canvas.getContext('2d'),
                    viewport,
                    transform: pixelRatio > 1 ? [pixelRatio, 0, 0, pixelRatio, 0, 0] : null,
                }).promise;
            } catch (err) {
                console.error(`PDF第${pageNum}页渲染失败:`, err);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.pdf-viewer {
    width: 100%;
    height: 100%;

    &__container {
        width: 100%;
        overflow-y: auto;
        text-align: center;
        background-color: #f5f5f5;
    }

    &__page {
        display: block;
        margin: 0 auto 10px;
        max-width: 100%;
        height: auto;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &__error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 200px;
        color: #999;

        p {
            margin-bottom: 12px;
        }
    }

    &__download {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        text-align: right;
        border-top: 1px solid #e5e5e5;
        line-height: 40px;
        padding-bottom: 15px;
        display: flex;
        align-items: center;
        justify-content: center;
        img {
            width: 20px;
            height: 20px;
            margin-right: 5px;
        }
    }

    &__download-button {
        cursor: pointer;
        font-size: 14px;
        color: #01A65C;
        padding: 0 10px;
        border-radius: 20px;
    }

    &__download-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
</style>
